import React, { useEffect, useRef } from 'react';

// Cloudflare Turnstile (managed) widget with explicit rendering.
// Calls onVerify(token) on success, and onError() if it errors or can't load in time.
//
// Callbacks are kept in refs so the widget is rendered exactly ONCE. Passing
// inline callbacks straight into the effect deps caused the widget to be
// removed + re-rendered on every parent re-render (flicker/loop).
export const Turnstile = ({ siteKey, onVerify, onError }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);

  // keep latest callbacks without re-running the render effect
  useEffect(() => {
    onVerifyRef.current = onVerify;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    let pollId = null;
    let failTimer = null;

    const fail = () => { if (!cancelled && onErrorRef.current) onErrorRef.current(); };

    const render = () => {
      if (cancelled || !window.turnstile || !containerRef.current || widgetIdRef.current !== null) return;
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'light',
          retry: 'never',
          callback: (token) => onVerifyRef.current && onVerifyRef.current(token),
          'expired-callback': () => onVerifyRef.current && onVerifyRef.current(null),
          'timeout-callback': fail,
          'error-callback': fail,
        });
      } catch (e) {
        fail();
      }
    };

    if (window.turnstile) {
      render();
    } else {
      if (!document.getElementById('cf-turnstile-script')) {
        const s = document.createElement('script');
        s.id = 'cf-turnstile-script';
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        s.async = true;
        s.defer = true;
        s.onerror = fail;
        document.head.appendChild(s);
      }
      pollId = setInterval(() => {
        if (window.turnstile) {
          clearInterval(pollId);
          pollId = null;
          render();
        }
      }, 200);
      setTimeout(() => pollId && clearInterval(pollId), 12000);
    }

    // If no token is obtained within 12s, assume the widget is unavailable.
    failTimer = setTimeout(fail, 12000);

    return () => {
      cancelled = true;
      if (pollId) clearInterval(pollId);
      if (failTimer) clearTimeout(failTimer);
      if (window.turnstile && widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (e) { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]); // render once per siteKey — callbacks read from refs

  return <div ref={containerRef} />;
};
